import React, { useState, useRef } from 'react';
import { Motor, Battery, Propeller, SystemConfig, AnalysisResult } from '../types';
import { exportProjectData, importProjectData, exportToExcel, exportMotors, exportBatteries, exportPropellers, importMotors, importBatteries, importPropellers } from '../utils/excelUtils';
import { SectionCard } from './Card';
import { Button } from './ButtonGroup';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';

interface DataManagementTabProps {
  motors: Motor[];
  batteries: Battery[];
  propellers: Propeller[];
  config: SystemConfig;
  analysisResults: AnalysisResult[];
  onMotorsChange: (motors: Motor[]) => void;
  onBatteriesChange: (batteries: Battery[]) => void;
  onPropellersChange: (propellers: Propeller[]) => void;
  onConfigChange: (config: SystemConfig) => void;
  onAnalysisResultsChange: (results: AnalysisResult[]) => void;
  showStatus: (message: string, type?: 'success' | 'warning' | 'error') => void;
}

const DataManagementTab: React.FC<DataManagementTabProps> = ({
  motors,
  batteries,
  propellers,
  config,
  analysisResults,
  onMotorsChange,
  onBatteriesChange,
  onPropellersChange,
  onConfigChange,
  onAnalysisResultsChange,
  showStatus
}) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const [reportFilename, setReportFilename] = useState(t('dataManagement.defaultReportName'));
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const motorFileInputRef = useRef<HTMLInputElement>(null);
  const batteryFileInputRef = useRef<HTMLInputElement>(null);
  const propellerFileInputRef = useRef<HTMLInputElement>(null);

  const handleExportProjectData = async () => {
    try {
      setIsExporting(true);
      const filename = exportProjectData(motors, batteries, propellers, config);
      showStatus(t('dataManagement.messages.projectExported', { 
        filename, 
        motors: motors.length, 
        batteries: batteries.length, 
        propellers: propellers.length 
      }));
    } catch (error) {
      showStatus(t('dataManagement.messages.exportFailed', { error: (error as Error).message }), 'error');
    } finally {
      setIsExporting(false);
    }
  };

  const handleImportProjectData = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsImporting(true);
      const importedData = await importProjectData(file);
      
      // 更新所有数据
      if (importedData.motors.length > 0) {
        onMotorsChange(importedData.motors);
      }
      if (importedData.batteries.length > 0) {
        onBatteriesChange(importedData.batteries);
      }
      if (importedData.propellers.length > 0) {
        onPropellersChange(importedData.propellers);
      }
      if (importedData.config) {
        onConfigChange({ ...config, ...importedData.config });
      }

      // 清除分析结果
      onAnalysisResultsChange([]);

      const importSummary = [
        importedData.motors.length > 0 ? `${importedData.motors.length}个电机` : '',
        importedData.batteries.length > 0 ? `${importedData.batteries.length}个电池` : '',
        importedData.propellers.length > 0 ? `${importedData.propellers.length}个桨叶` : '',
        importedData.config ? '系统配置已更新' : ''
      ].filter(Boolean).join(', ');

      showStatus(`项目数据导入成功: ${importSummary}`);
    } catch (error) {
      showStatus('项目数据导入失败: ' + (error as Error).message, 'error');
    } finally {
      setIsImporting(false);
      // 清空文件输入
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleExportAnalysisReport = () => {
    try {
      setIsExporting(true);
      exportToExcel(motors, batteries, propellers, config, analysisResults, reportFilename);
      showStatus(`分析报告已导出: ${reportFilename}.xlsx`);
    } catch (error) {
      showStatus('分析报告导出失败: ' + (error as Error).message, 'error');
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportSelectedConfigurations = () => {
    if (analysisResults.length === 0) {
      showStatus('请先计算分析结果', 'warning');
      return;
    }

    try {
      // 导出前5名配置
      const topConfigurations = analysisResults.slice(0, 5);
      
      // 这里可以复用现有的导出逻辑
      const filename = '最优配置方案_' + new Date().toISOString().split('T')[0];
      exportToExcel(motors, batteries, propellers, config, topConfigurations, filename);
      
      showStatus('已导出前5名优选配置方案');
    } catch (error) {
      showStatus('优选配置导出失败: ' + (error as Error).message, 'error');
    }
  };

  // 单独组件导出功能
  const handleExportMotors = () => {
    try {
      const filename = exportMotors(motors);
      showStatus(`电机数据已导出: ${filename}.xlsx (包含${motors.length}个电机)`);
    } catch (error) {
      showStatus('电机数据导出失败: ' + (error as Error).message, 'error');
    }
  };

  const handleExportBatteries = () => {
    try {
      const filename = exportBatteries(batteries);
      showStatus(`电池数据已导出: ${filename}.xlsx (包含${batteries.length}个电池)`);
    } catch (error) {
      showStatus('电池数据导出失败: ' + (error as Error).message, 'error');
    }
  };

  const handleExportPropellers = () => {
    try {
      const filename = exportPropellers(propellers);
      showStatus(`桨叶数据已导出: ${filename}.xlsx (包含${propellers.length}个桨叶)`);
    } catch (error) {
      showStatus('桨叶数据导出失败: ' + (error as Error).message, 'error');
    }
  };

  // 单独组件导入功能
  const handleImportMotors = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsImporting(true);
      const importedMotors = await importMotors(file);
      onMotorsChange(importedMotors);
      showStatus(`电机数据导入成功: ${importedMotors.length}个电机`);
    } catch (error) {
      showStatus('电机数据导入失败: ' + (error as Error).message, 'error');
    } finally {
      setIsImporting(false);
      if (motorFileInputRef.current) {
        motorFileInputRef.current.value = '';
      }
    }
  };

  const handleImportBatteries = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsImporting(true);
      const importedBatteries = await importBatteries(file);
      onBatteriesChange(importedBatteries);
      showStatus(t('battery.messages.imported', { count: importedBatteries.length }));
    } catch (error) {
      showStatus(t('battery.messages.importError', { error: (error as Error).message }), 'error');
    } finally {
      setIsImporting(false);
      if (batteryFileInputRef.current) {
        batteryFileInputRef.current.value = '';
      }
    }
  };

  const handleImportPropellers = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsImporting(true);
      const importedPropellers = await importPropellers(file);
      onPropellersChange(importedPropellers);
      showStatus(t('propeller.messages.imported', { count: importedPropellers.length }));
    } catch (error) {
      showStatus(t('propeller.messages.importError', { error: (error as Error).message }), 'error');
    } finally {
      setIsImporting(false);
      if (propellerFileInputRef.current) {
        propellerFileInputRef.current.value = '';
      }
    }
  };



    return (
    <div className="space-y-6">
      {/* 项目数据管理 */}
      <SectionCard 
        title={t('dataManagement.title')}
        variant="standard"
      >
        <div className="space-y-6">
          {/* 数据概览 */}
          <div>
            <h3 className="text-base font-medium mb-4" style={{ color: colors.text }}>
              {t('dataManagement.dataOverview')}
            </h3>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div 
                className="text-center p-3 rounded-md border"
                style={{ 
                  backgroundColor: colors.surface,
                  borderColor: colors.border 
                }}
              >
                <div className="text-xl font-semibold mb-1" style={{ color: colors.text }}>
                  {motors.length}
                </div>
                <div className="text-xs" style={{ color: colors.textSecondary }}>
                  {t('dataManagement.motorData')}
                </div>
              </div>
              
              <div 
                className="text-center p-3 rounded-md border"
                style={{ 
                  backgroundColor: colors.surface,
                  borderColor: colors.border 
                }}
              >
                <div className="text-xl font-semibold mb-1" style={{ color: colors.text }}>
                  {batteries.length}
                </div>
                <div className="text-xs" style={{ color: colors.textSecondary }}>
                  {t('dataManagement.batteryData')}
                </div>
              </div>
              
              <div 
                className="text-center p-3 rounded-md border"
                style={{ 
                  backgroundColor: colors.surface,
                  borderColor: colors.border 
                }}
              >
                <div className="text-xl font-semibold mb-1" style={{ color: colors.text }}>
                  {propellers.length}
                </div>
                <div className="text-xs" style={{ color: colors.textSecondary }}>
                  {t('dataManagement.propellerData')}
                </div>
              </div>
              
              <div 
                className="text-center p-3 rounded-md border"
                style={{ 
                  backgroundColor: colors.surface,
                  borderColor: colors.border 
                }}
              >
                <div className="text-xl font-semibold mb-1" style={{ color: colors.text }}>
                  {analysisResults.length}
                </div>
                <div className="text-xs" style={{ color: colors.textSecondary }}>
                  {t('dataManagement.analysisResults')}
                </div>
              </div>
            </div>
          </div>

          {/* 分隔线 */}
          <div 
            className="border-t"
            style={{ borderColor: colors.borderLight }}
          />

          {/* 项目总览 */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-base font-medium mb-1" style={{ color: colors.text }}>
                {t('dataManagement.fullProjectData')}
              </h3>
              <p className="text-sm" style={{ color: colors.textSecondary }}>
                {t('dataManagement.fullProjectDescription')}
              </p>
            </div>
            
            <div className="flex items-center space-x-3">
              <Button
                onClick={handleExportProjectData}
                disabled={isExporting}
                variant="success"
                size="sm"
              >
                {isExporting ? '导出中...' : '导出项目数据'}
              </Button>
              
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={isImporting}
                variant="primary"
                size="sm"
              >
                {isImporting ? '导入中...' : '导入项目数据'}
              </Button>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleImportProjectData}
              style={{ display: 'none' }}
              aria-label="导入项目数据文件"
            />
          </div>

          {/* 分隔线 */}
          <div 
            className="border-t"
            style={{ borderColor: colors.borderLight }}
          />

          {/* 组件数据管理 */}
          <div>
            <h3 className="text-base font-medium mb-4" style={{ color: colors.text }}>
              {t('dataManagement.componentDataManagement')}
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div 
                className="p-4 rounded-md border"
                style={{ 
                  backgroundColor: colors.surface,
                  borderColor: colors.border 
                }}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <div 
                      className="w-6 h-6 rounded flex items-center justify-center text-xs font-medium"
                      style={{ 
                        backgroundColor: colors.primary + '10',
                        color: colors.primary
                      }}
                    >
                      M
                    </div>
                    <div>
                      <div className="text-sm font-medium" style={{ color: colors.text }}>
                        {t('dataManagement.motorConfig')}
                      </div>
                      <div className="text-xs" style={{ color: colors.textSecondary }}>
                        {t('dataManagement.recordCount', { count: motors.length })}
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex space-x-2">
                  <Button
                    onClick={handleExportMotors}
                    disabled={motors.length === 0 || isExporting}
                    variant="secondary"
                    size="sm"
                    className="flex-1"
                  >
                    {t('dataManagement.export')}
                  </Button>
                  
                  <Button
                    onClick={() => motorFileInputRef.current?.click()}
                    disabled={isImporting}
                    variant="secondary"
                    size="sm"
                    className="flex-1"
                  >
                    {t('dataManagement.import')}
                  </Button>
                </div>

                <input
                  ref={motorFileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleImportMotors}
                  style={{ display: 'none' }}
                  aria-label={t('dataManagement.importMotorData')}
                />
              </div>

              <div 
                className="p-4 rounded-md border"
                style={{ 
                  backgroundColor: colors.surface,
                  borderColor: colors.border 
                }}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <div 
                      className="w-6 h-6 rounded flex items-center justify-center text-xs font-medium"
                      style={{ 
                        backgroundColor: colors.warning + '10',
                        color: colors.warning
                      }}
                    >
                      B
                    </div>
                    <div>
                      <div className="text-sm font-medium" style={{ color: colors.text }}>
                        {t('dataManagement.batteryConfig')}
                      </div>
                      <div className="text-xs" style={{ color: colors.textSecondary }}>
                        {t('dataManagement.recordCount', { count: batteries.length })}
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex space-x-2">
                  <Button
                    onClick={handleExportBatteries}
                    disabled={batteries.length === 0 || isExporting}
                    variant="secondary"
                    size="sm"
                    className="flex-1"
                  >
                    {t('dataManagement.export')}
                  </Button>
                  
                  <Button
                    onClick={() => batteryFileInputRef.current?.click()}
                    disabled={isImporting}
                    variant="secondary"
                    size="sm"
                    className="flex-1"
                  >
                    {t('dataManagement.import')}
                  </Button>
                </div>

                <input
                  ref={batteryFileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleImportBatteries}
                  style={{ display: 'none' }}
                  aria-label={t('dataManagement.importBatteryData')}
                />
              </div>

              <div 
                className="p-4 rounded-md border"
                style={{ 
                  backgroundColor: colors.surface,
                  borderColor: colors.border 
                }}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <div 
                      className="w-6 h-6 rounded flex items-center justify-center text-xs font-medium"
                      style={{ 
                        backgroundColor: colors.accent + '10',
                        color: colors.accent
                      }}
                    >
                      P
                    </div>
                    <div>
                      <div className="text-sm font-medium" style={{ color: colors.text }}>
                        {t('dataManagement.propellerConfig')}
                      </div>
                      <div className="text-xs" style={{ color: colors.textSecondary }}>
                        {t('dataManagement.recordCount', { count: propellers.length })}
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex space-x-2">
                  <Button
                    onClick={handleExportPropellers}
                    disabled={propellers.length === 0 || isExporting}
                    variant="secondary"
                    size="sm"
                    className="flex-1"
                  >
                    {t('dataManagement.export')}
                  </Button>
                  
                  <Button
                    onClick={() => propellerFileInputRef.current?.click()}
                    disabled={isImporting}
                    variant="secondary"
                    size="sm"
                    className="flex-1"
                  >
                    {t('dataManagement.import')}
                  </Button>
                </div>

                <input
                  ref={propellerFileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleImportPropellers}
                  style={{ display: 'none' }}
                  aria-label={t('dataManagement.importPropellerData')}
                />
              </div>
            </div>
          </div>

          {/* 分隔线 */}
          <div 
            className="border-t"
            style={{ borderColor: colors.borderLight }}
          />

          {/* 分析报告导出 */}
          <div>
            <h3 className="text-base font-medium mb-4" style={{ color: colors.text }}>
              {t('dataManagement.analysisReportExport')}
            </h3>
            
            <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-end">
              <div className="flex-1">
                <label 
                  htmlFor="filename" 
                  className="block text-sm font-medium mb-2"
                  style={{ color: colors.text }}
                >
                  {t('dataManagement.fileName')}
                </label>
                <input
                  id="filename"
                  type="text"
                  value={reportFilename}
                  onChange={(e) => setReportFilename(e.target.value)}
                  placeholder={t('dataManagement.enterFileName')}
                  className="w-full px-3 py-2 border rounded-md text-sm"
                  style={{
                    backgroundColor: colors.surface,
                    borderColor: colors.border,
                    color: colors.text
                  }}
                />
              </div>
              
              <div className="flex gap-3">
                <Button
                  onClick={handleExportAnalysisReport}
                  disabled={isExporting}
                  variant="success"
                  size="sm"
                >
                  {isExporting ? t('dataManagement.generating') : t('dataManagement.exportFullReport')}
                </Button>
                
                <Button
                  onClick={handleExportSelectedConfigurations}
                  disabled={analysisResults.length === 0}
                  variant="secondary"
                  size="sm"
                >
                  {t('dataManagement.exportOptimalConfigs')}
                </Button>
              </div>
            </div>

            {analysisResults.length === 0 && (
              <div 
                className="mt-4 text-center py-6 rounded-md border border-dashed text-sm"
                style={{ 
                  borderColor: colors.borderLight,
                  color: colors.textSecondary
                }}
              >
                {t('dataManagement.noAnalysisResults')}
              </div>
            )}
          </div>
        </div>
      </SectionCard>
    </div>
  );
};

export default DataManagementTab; 